import { getActiveLocale } from './activeLocale'

/**
 * The sizes and media durations the customer is billed on, in the displayed language.
 *
 * **One formatter, so a size reads the same on every screen that prices it** — the storage card,
 * its details, the consumption screen and the home figures. It used to print `3.2 GB` on a French
 * page: the units were English constants and the decimal point was `toFixed`'s. `Intl` gives each
 * language its own units and separators, so French reads `3,2 Go`, `1 To`, `640 Mo` and
 * `12 h 34 min`, and English `3.2 GB` and `12 hr 34 min`, with nothing to translate here.
 *
 * Both take the locale explicitly when the caller has it (`useI18n().locale.value`), and fall back
 * to the displayed one otherwise — see `activeLocale.ts` for why a bare default is not enough inside
 * a `computed`.
 */

/** The `Intl` units, decimal: storage and delivery are priced per 10^9 bytes, never per 2^30. */
const BYTE_UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte', 'petabyte'] as const

/**
 * Bytes the customer is billed on, in decimal (SI) units.
 *
 * Storage and delivery are both priced per 10^9 bytes, so `formatFileSize` — which divides by
 * 1024 — puts a 6.9% gap at gigabyte scale and 9.1% at terabyte scale between what the screen
 * shows and what the invoice charges. Use this for organization and workspace totals and their
 * breakdowns; keep `formatFileSize` for individual file sizes, where the filesystem convention is
 * what users expect.
 *
 * Trailing zeros are dropped: `1 To`, not `1,0 To`.
 *
 * @param bytes - The number of bytes; anything not positive reads as zero
 * @param decimals - The most decimal places shown above the byte (default 1)
 * @param locale - The locale to format in; defaults to the displayed language
 *
 * @example
 * formatBilledSize(3_200_000_000, 1, 'fr') // '3,2 Go'
 * formatBilledSize(1_000_000_000_000, 1, 'fr') // '1 To'
 * formatBilledSize(3_200_000_000, 1, 'en') // '3.2 GB'
 */
export function formatBilledSize(bytes: number, decimals = 1, locale?: string): string {
  const value = Number.isFinite(bytes) && bytes > 0 ? bytes : 0
  let index = value < 1 ? 0 : Math.min(BYTE_UNITS.length - 1, Math.floor(Math.log10(value) / 3))
  // 999 960 000 bytes would round to « 1 000 Mo »: the next unit reads it as « 1 Go ».
  const shown = (at: number) => Number((value / 1000 ** at).toFixed(at === 0 ? 0 : decimals))
  if (index < BYTE_UNITS.length - 1 && shown(index) >= 1000) index += 1

  return new Intl.NumberFormat(locale ?? getActiveLocale(), {
    style: 'unit',
    unit: BYTE_UNITS[index],
    unitDisplay: 'short',
    maximumFractionDigits: index === 0 ? 0 : decimals,
  }).format(value / 1000 ** index)
}

/**
 * A media duration as the storage figures show it: hours and minutes, the seconds dropped.
 *
 * Video storage is priced by the hour, so this is the figure a price is read against. Zero reads
 * as zero hours rather than as nothing.
 *
 * @param seconds - The duration in seconds; anything not positive reads as zero
 * @param locale - The locale to format in; defaults to the displayed language
 *
 * @example
 * formatMediaDuration(45_240, 'fr') // '12 h 34 min'
 * formatMediaDuration(2_700, 'fr') // '45 min'
 * formatMediaDuration(45_240, 'en') // '12 hr 34 min'
 */
export function formatMediaDuration(seconds: number, locale?: string): string {
  const totalMinutes = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds / 60) : 0
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const code = locale ?? getActiveLocale()
  const unit = (amount: number, name: 'hour' | 'minute') =>
    new Intl.NumberFormat(code, { style: 'unit', unit: name, unitDisplay: 'short' }).format(amount)

  if (hours === 0 && minutes > 0) return unit(minutes, 'minute')
  if (minutes === 0) return unit(hours, 'hour')
  return `${unit(hours, 'hour')} ${unit(minutes, 'minute')}`
}
